import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";
import Annotation from "chartjs-plugin-annotation";
import { useDispatch, useSelector } from "react-redux";

import { PredictionChartButton, LineChartTooltip } from "@/components/dataPages";
import { NoData, TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { Skeleton } from "@/components/common/other/Skeleton";
import { ErrorComponent } from "@/components/common/errors";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { useQuery } from "@/utils/query";
import { getMonthName } from "@/utils/date";
import { clearRateHistoryState, getRateHistory } from "@/state/rateHistorySlice";
import { cn } from "@/utils/css";
import { calculateMaxValue, calculateMinValue } from "@/utils/graph";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, Filler, Annotation);

export const PredictionLineChart = ({ withFlat }) => {
    const [selectedRange, setSelectedRange] = useState(withFlat ? "flat" : "perMile");
    const dispatch = useDispatch();
    const { data: rateData, loading, error } = useSelector((state) => state.rateHistory);
    const isDarkTheme = useIsDarkTheme();
    const { pickupZip, deliveryZip, trailerType } = useQuery();
    const [tooltipData, setTooltipData] = useState({
        x: 0,
        y: 0,
        value: "",
        visible: false,
        maxValue: 0,
        minValue: 0,
    });

    useEffect(() => {
        dispatch(getRateHistory({ pickupZip, deliveryZip, trailerType }));
    }, [pickupZip, deliveryZip, trailerType]);

    const perMileData = useMemo(
        () => (rateData ? rateData.map((r) => (r.avgRPM ? parseFloat(r.avgRPM?.toFixed(2)) : null)) : []),
        [rateData],
    );
    const flatData = useMemo(() => (rateData ? rateData.map((r) => (r.avgPrice ? parseInt(r.avgPrice) : null)) : []), [rateData]);
    const generateMonthArray = () => {
        const currentMonth = new Date().getMonth() + 1; // Get current month (1-12)
        const months = [];

        for (let i = 0; i < 12; i++) {
            months.push(getMonthName(((currentMonth + i - 1) % 12) + 1));
        }
        months.push(getMonthName(currentMonth));

        return months;
    };
    const defaultMonths = generateMonthArray();
    const months = useMemo(() => (rateData.length ? rateData.map((d) => getMonthName(d.month)) : defaultMonths), [rateData]);

    const getChartData = () => {
        switch (selectedRange) {
            case "perMile":
                return perMileData;
            case "flat":
            default:
                return flatData;
        }
    };

    const maxValue = useMemo(() => calculateMaxValue(getChartData()), [flatData, perMileData, selectedRange]);
    const minValue = useMemo(() => calculateMinValue(getChartData()), [flatData, perMileData, selectedRange]);
    const stepSize = useMemo(() => (selectedRange === "flat" ? 100 : 0.5), [selectedRange]);

    const minTickValue = useMemo(() => {
        if (!isFinite(minValue) || !minValue) return 0;
        const tempMin = Math.floor(minValue / stepSize) * stepSize;
        return tempMin < 0 ? 0 : tempMin;
    }, [minValue, stepSize]);

    const maxTickValue = useMemo(() => {
        if (!isFinite(maxValue) || !maxValue) return stepSize * 5; // Default to 5 steps
        const calculatedMax = Math.round(maxValue / stepSize + 1) * stepSize;
        // Ensure there's a reasonable range between min and max
        return calculatedMax > minTickValue ? calculatedMax : minTickValue + stepSize * 5;
    }, [maxValue, stepSize, minTickValue]);

    const externalTooltipHandler = (context) => {
        const { chart, tooltip } = context;
        if (!tooltip || tooltip.opacity === 0) {
            setTooltipData((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            return;
        }

        const tooltipWidth = 88;
        const tooltipHeight = 72;

        const chartWidth = chart.width;
        const chartHeight = chart.height;

        let x = tooltip.caretX + 6 + 3; // 6 is hover radius, 3 is padding
        let y = tooltip.caretY - tooltipHeight / 2; // Center vertically

        // Shift left if too close to right edge
        if (x + tooltipWidth > chartWidth) {
            x = tooltip.caretX - tooltipWidth - 6 - 3;
        }

        // Shift up if too close to bottom edge
        if (y + tooltipHeight > chartHeight) {
            y = y - (y + tooltipHeight - chartHeight);
        }

        const label = tooltip.body?.[0]?.lines?.[0] || "";
        const round = selectedRange === "flat";

        const newData = {
            x,
            y,
            value: label,
            visible: true,
            maxValue: new Intl.NumberFormat("en-US", { minimumFractionDigits: round ? 0 : 2 }).format(
                round ? Math.round(maxValue) : maxValue,
            ),
            minValue: new Intl.NumberFormat("en-US", { minimumFractionDigits: round ? 0 : 2 }).format(
                round ? Math.round(minValue) : minValue,
            ),
        };

        setTooltipData((prev) => {
            if (prev.x !== newData.x || prev.y !== newData.y || prev.value !== newData.value || prev.visible !== newData.visible) {
                return newData;
            }
            return prev;
        });
    };

    const chartData = {
        labels: months,
        datasets:
            maxValue && maxValue > 0
                ? [
                      {
                          data: selectedRange === "perMile" ? perMileData : flatData,
                          borderColor: "#24B98C",
                          backgroundColor: "transparent",
                          fill: true,
                          pointRadius: 3,
                          hoverRadius: 6,
                          pointBackgroundColor: "#24B98C",
                          pointHoverBackgroundColor: "#2BBC90",
                          borderWidth: 2,
                          pointBorderWidth: 3,
                          pointHoverBorderColor: "#121815",
                          pointBorderColor: "#24B98C",
                          datalabels: {
                              display: false,
                          },
                      },
                  ]
                : [],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        height: 285,
        plugins: {
            interaction: {
                mode: "nearest",
                intersect: false,
            },

            legend: {
                display: false,
            },

            title: {
                display: false,
            },

            tooltip: {
                mode: "nearest",
                intersect: false,
                enabled: false,
                external: externalTooltipHandler,
            },
        },
        hover: {
            mode: "nearest",
            intersect: false,
        },

        scales: {
            x: {
                title: {
                    display: false,
                },

                ticks: {
                    intersect: true,
                    font: {
                        size: 14,
                        family: "Saira",
                    },
                    crossAlign: "near",
                    color: isDarkTheme ? "#838A85" : "#979B98",
                },

                grid: {
                    display: false,
                },

                border: {
                    display: false,
                },
            },

            y: {
                display: !!maxValue,
                title: {
                    display: false,
                },
                min: minTickValue,
                max: maxTickValue,
                ticks: {
                    color: isDarkTheme ? "#838A85" : "#979B98",
                    padding: selectedRange === "perMile" ? 10 : 5,
                    crossAlign: "far",
                    font: {
                        size: 14,
                        family: "Saira",
                    },
                    stepSize,
                    maxTicksLimit: 5,
                    callback: (value) => {
                        if (selectedRange === "flat") {
                            return new Intl.NumberFormat("en-US").format(value ? Math.round(value) : value);
                        }

                        return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2 }).format(value);
                    },
                },

                grid: {
                    color: isDarkTheme ? "#282928" : "#E5E9EB",
                    display: !!maxValue,
                    drawTicks: false,
                    drawBorder: false,
                },
            },
        },
        animation: {
            duration: 300,
            easing: "easeInOutQuad",
        },
    };

    useEffect(() => {
        return () => {
            dispatch(clearRateHistoryState());
        };
    }, []);

    return (
        <div className="flex flex-col pt-4 border-t border-cm-p-outline-variation lg:pt-0 lg:border-0 mt-0 sm:mt-4 lg:mt-0">
            <div className="flex gap-4 md:gap-0 flex-row items-center justify-between pb-9">
                <div className="text-cm-p-text-primary text-base/[22px] flex items-center">
                    <TranslatedText>Rate history</TranslatedText>
                    <div className="ml-2">
                        <InfoButton text="Rate history provides insights into how rates have changed over time for this lane." />
                    </div>
                </div>
                <div className="flex flex-col gap-4 md:gap-1 md:flex-row items-center ml-12">
                    {withFlat ? (
                        <>
                            <div className="flex text-cm-text-secondary text-sm border border-cm-p-outline-variation rounded-[58px]">
                                <PredictionChartButton
                                    option={{
                                        name: "Flat",
                                        value: "flat",
                                    }}
                                    activeValue={selectedRange}
                                    setActiveValue={setSelectedRange}
                                />
                                <PredictionChartButton
                                    option={{
                                        name: "Per mile",
                                        value: "perMile",
                                    }}
                                    activeValue={selectedRange}
                                    setActiveValue={setSelectedRange}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
            <div className="relative flex-1 w-full h-[285px] min-h-[285px]">
                <div className="h-[285px] min-h-[285px] w-full ">
                    <Line data={chartData} options={options} />
                </div>
                <LineChartTooltip {...tooltipData} />
                {loading || error || (getChartData().length && maxValue !== 0) ? null : (
                    <NoData className="absolute top-20 text-center w-full pl-12 pr-4" />
                )}
                {/*Commented out skeleton to discuss with team */}
                {/*<Skeleton*/}
                {/*    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0 opacity-50", {*/}
                {/*        hidden: !loading,*/}
                {/*    })}*/}
                {/*/>*/}
                <ErrorComponent
                    className={cn("min-h-full absolute top-0 left-0 right-0 bottom-0", {
                        hidden: !error,
                    })}
                />
            </div>
        </div>
    );
};
